import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import LiveTv from '@mui/icons-material/LiveTv';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import MenuBook from '@mui/icons-material/MenuBook';
import MovieCreation from '@mui/icons-material/MovieCreation';
import Hub from '@mui/icons-material/Hub';
import { enProduccionConMemForegroundOnPage } from '../enProduccionConMemReadableText';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';


const ICON_MAP = {
  liveTv: LiveTv,
  recordVoiceOver: RecordVoiceOver,
  menuBook: MenuBook,
  movieCreation: MovieCreation,
  hub: Hub,
};

const ICON_KEYS = Object.keys(ICON_MAP);

/**
 * Bloque tipo Colegio de Abogados: cards que explican cada ítem del plan antes de la tabla resumen.
 */
const EnProduccionConMemProposalPlanCards = ({
  themeMode = 'light',
  overline,
  title,
  subtitle,
  body,
  cards,
}) => {
  const theme = useTheme();
  const headingColor = enProduccionConMemForegroundOnPage(theme);
  const accent = theme.palette.secondary.main;
  const deep = theme.palette.primary.main;

  return (
    <Box component="section" sx={{ my: { xs: 4, md: 5 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 4,
            minHeight: { xs: 48, sm: 56 },
            borderRadius: 2,
            background: `linear-gradient(180deg, ${accent} 0%, ${deep} 100%)`,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            variant="overline"
            sx={{ color: accent, fontWeight: 700, letterSpacing: '0.16em', display: 'block', mb: 0.5 }}
          >
            {overline}
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              color: headingColor,
              fontFamily: 'Georgia, "Times New Roman", serif',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="subtitle1"
              sx={{
                mt: 1.25,
                fontWeight: 800,
                color: deep,
                letterSpacing: '0.02em',
                fontFeatureSettings: '"tnum"',
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
          {body ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 1.5, maxWidth: 720, lineHeight: 1.75 }}>
              {body}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {cards.map(({ service, priceUsd, details, benefit, icon }) => {
          const Icon = ICON_MAP[icon] || LiveTv;
          return (
            <Grid item xs={12} sm={6} md={4} key={service}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                  borderLeft: `4px solid ${accent}`,
                  bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff',
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0 8px 32px rgba(0,0,0,0.25)'
                      : '0 8px 30px rgba(0,0,0,0.06)',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-3px)' },
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    background:
                      theme.palette.mode === 'dark'
                        ? `linear-gradient(120deg, rgba(255,255,255,0.06) 0%, transparent 55%)`
                        : `linear-gradient(120deg, rgba(0,0,0,0.03) 0%, transparent 50%)`,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: deep,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                    }}
                  >
                    <Icon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: typeof priceUsd === 'number' ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
                        columnGap: 1.5,
                        rowGap: 0.5,
                        alignItems: 'start',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          color: headingColor,
                          lineHeight: 1.35,
                          minWidth: 0,
                        }}
                      >
                        {service}
                      </Typography>
                      {typeof priceUsd === 'number' ? (
                        <Box
                          sx={{
                            justifySelf: 'end',
                            alignSelf: 'start',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 2,
                            border: `1px solid ${accent}`,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                            textAlign: 'right',
                          }}
                        >
                          <Typography
                            component="span"
                            variant="subtitle2"
                            sx={{ fontWeight: 800, color: accent, lineHeight: 1.2 }}
                          >
                            ${priceUsd}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                            sx={{ ml: 0.5, lineHeight: 1.2, whiteSpace: 'nowrap' }}
                          >
                            USD/mes
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        mt: 1,
                        pl: 2.25,
                        listStyleType: 'disc',
                        color: 'text.secondary',
                        '& li': { mb: 0.75, lineHeight: 1.65, pl: 0.35 },
                        '& li:last-of-type': { mb: 0 },
                      }}
                    >
                      {details.map((item) => (
                        <Typography key={item} component="li" variant="body2" color="textSecondary">
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: 'auto',
                    px: 2.5,
                    py: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.08em' }}>
                    Valor agregado
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: headingColor, mt: 0.5, lineHeight: 1.55 }}>
                    {benefit}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <PriceVatDisclaimer />
    </Box>
  );
};

EnProduccionConMemProposalPlanCards.propTypes = {
  themeMode: PropTypes.string,
  overline: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  body: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      service: PropTypes.string.isRequired,
      priceUsd: PropTypes.number,
      details: PropTypes.arrayOf(PropTypes.string).isRequired,
      benefit: PropTypes.string.isRequired,
      icon: PropTypes.oneOf(ICON_KEYS).isRequired,
    })
  ).isRequired,
};

export default EnProduccionConMemProposalPlanCards;
